<script setup lang="ts">
import type { CampusLinkedEvent } from '@/services/campusLinkedEventsService'

import { getCampusLinkedEvents } from '@/services/campusLinkedEventsService'
import { getEventTypeLabel } from '@/utils/eventTypeSettingsProfile'

// Stable link Campus and campusapi use for "SignMeUp events of this Campus course".
// One event redirects straight to it; several are listed.
definePage({
  meta: {
    requiresAuth: false,
  },
})

const route = useRoute('/campus/[guid]')
const router = useRouter()
const { t, d, locale } = useI18n()
const { setPageTitle } = useBreadcrumbs()

const events = ref<CampusLinkedEvent[]>([])
const loading = ref(true)
const failed = ref(false)

const isGerman = computed(() => locale.value.startsWith('de'))

const stateColor = {
  open: 'success',
  upcoming: 'info',
  closed: 'neutral',
} as const

function eventName(event: CampusLinkedEvent) {
  return (isGerman.value ? event.nameDE : event.nameEN) || event.nameDE
}

function eventCaption(event: CampusLinkedEvent) {
  return (isGerman.value ? event.captionDE : event.captionEN) || event.captionDE
}

async function load() {
  loading.value = true
  failed.value = false
  try {
    events.value = await getCampusLinkedEvents(route.params.guid)
    const [single] = events.value
    if (events.value.length === 1 && single) {
      await router.replace(`/event/${single.id}`)
    }
  } catch {
    failed.value = true
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  setPageTitle('pages.campus.title')
  void load()
})
</script>

<template>
  <div class="mx-auto flex max-w-3xl flex-col gap-4 p-4">
    <div>
      <h1 class="text-2xl font-semibold">{{ t('pages.campus.title') }}</h1>
      <p class="text-muted">{{ t('pages.campus.subtitle') }}</p>
    </div>

    <div v-if="loading" class="flex flex-col gap-3">
      <USkeleton v-for="i in 3" :key="i" class="h-24 w-full" />
    </div>

    <UAlert
      v-else-if="failed"
      color="error"
      variant="subtle"
      icon="i-lucide-circle-alert"
      :title="t('pages.campus.error')"
    />

    <UAlert
      v-else-if="events.length === 0"
      color="neutral"
      variant="subtle"
      icon="i-lucide-calendar-x"
      :title="t('pages.campus.empty')"
    />

    <template v-else>
      <RouterLink v-for="event in events" :key="event.id" :to="`/event/${event.id}`" class="block">
        <UCard class="transition-colors hover:bg-elevated/50">
          <div class="flex flex-col gap-2">
            <div class="flex flex-wrap items-start justify-between gap-2">
              <div class="min-w-0">
                <p class="font-medium">{{ eventName(event) }}</p>
                <p v-if="eventCaption(event)" class="text-sm text-muted">
                  {{ eventCaption(event) }}
                </p>
              </div>
              <UBadge
                :color="stateColor[event.registrationState]"
                variant="subtle"
                :label="t(`pages.campus.state.${event.registrationState}`)"
              />
            </div>
            <div class="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted">
              <span>{{ getEventTypeLabel(event.eventType, t) }}</span>
              <span v-if="event.workspaceName">{{ event.workspaceName }}</span>
              <span v-if="event.registrationStart && event.registrationEnd">
                {{
                  t('pages.campus.registrationPeriod', {
                    start: d(new Date(event.registrationStart), 'datetime'),
                    end: d(new Date(event.registrationEnd), 'datetime'),
                  })
                }}
              </span>
            </div>
            <p v-if="event.linkedVia === 'slot' && event.linkedSlots.length" class="text-sm">
              {{
                t('pages.campus.linkedSlots', {
                  slots: event.linkedSlots.map((slot) => slot.name ?? `#${slot.id}`).join(', '),
                })
              }}
            </p>
          </div>
        </UCard>
      </RouterLink>
    </template>
  </div>
</template>
